/**
 * Main App Component
 *
 * Uses createBrowserRouter (data router) to enable useBlocker in form components.
 * All providers are rendered inside the router context via the AppLayout route.
 */

import { Suspense, useEffect } from "react"
import {
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
} from "react-router-dom"
import { Toaster } from "sonner"
import { RequireCustomerApp } from "./auth/RequireCustomerApp"
import { RequireGlobalAdmin } from "./auth/RequireGlobalAdmin"
import { RequirePlatformUser } from "./auth/RequirePlatformUser"
import { AdminEdgeGate } from "./components/auth/AdminEdgeGate"
import { AuthGuard } from "./components/auth/AuthGuard"
import { RootRedirect } from "./components/auth/RootRedirect"
import { PageLoader } from "./components/ui/page-loader"
import { AuthProvider } from "./contexts/AuthContext"
import { TenantProvider } from "./contexts/TenantContext"
import { useTheme } from "./hooks/useTheme"
import { AdminLayout } from "./layouts/AdminLayout"
import { RootLayout } from "./layouts/RootLayout"
import { lazyRoute } from "./lib/lazy-route"
import { adminSimulatorEnabled } from "./libs/admin-navigation"
import { CUSTOMER_ROUTE_PATTERN, legacyCustomerFleetRedirect } from "./libs/customer-paths"
import { installTopologySync } from "./libs/topology-sync"

// Lazy-loaded pages for code splitting
const LoginPage = lazyRoute(() => import("./pages/LoginPage"))
const ForgotPasswordPage = lazyRoute(() => import("./pages/ForgotPasswordPage"))
const ResetPasswordPage = lazyRoute(() => import("./pages/ResetPasswordPage"))
const CustomerPickerPage = lazyRoute(() => import("./pages/CustomerPickerPage"))
const AccountPage = lazyRoute(() => import("./pages/AccountPage"))
const OverviewPage = lazyRoute(() => import("./pages/OverviewPage"))
const ReportsPage = lazyRoute(() => import("./pages/ReportsPage"))
const VehiclesPage = lazyRoute(() => import("./pages/VehiclesPage"))
const VehicleDetailPage = lazyRoute(() => import("./pages/VehicleDetailPage"))
const TripSchedulePage = lazyRoute(() => import("./pages/TripSchedulePage"))
const SitesPage = lazyRoute(() => import("./pages/SitesPage"))
const SiteDetailPage = lazyRoute(() => import("./pages/SiteDetailPage"))
const SimulationsPage = lazyRoute(() => import("./pages/SimulationsPage"))
const OptimizerSimulatorPage = lazyRoute(() => import("./pages/admin/OptimizerSimulatorPage"))
const SimulationRunDetailPage = lazyRoute(() => import("./pages/SimulationRunDetailPage"))
const ChargersPage = lazyRoute(() => import("./pages/ChargersPage"))
const ChargerDetailPage = lazyRoute(() => import("./pages/ChargerDetailPage"))
const ChargingRequirementsPage = lazyRoute(() => import("./pages/ChargingRequirementsPage"))
const CustomerDevicesPage = lazyRoute(() => import("./pages/CustomerDevicesPage"))
const BatteriesPage = lazyRoute(() => import("./pages/BatteriesPage"))
const MicrogridPage = lazyRoute(() => import("./pages/MicrogridPage"))
const BessMaintenancePage = lazyRoute(() => import("./pages/BessMaintenancePage"))
const BatteryDetailPage = lazyRoute(() => import("./pages/BatteryDetailPage"))
const RfidCardsPage = lazyRoute(() => import("./pages/RfidCardsPage"))
const GridServicesPage = lazyRoute(() => import("./pages/GridServicesPage"))
const CompanySettingsPage = lazyRoute(() => import("./pages/CompanySettingsPage"))

// Admin pages
const AdminOverviewPage = lazyRoute(() => import("./pages/admin/AdminOverviewPage"))
const CustomersPage = lazyRoute(() => import("./pages/admin/CustomersPage"))
const AdminCustomerDetailPage = lazyRoute(() => import("./pages/admin/AdminCustomerDetailPage"))
const AdminUsersPage = lazyRoute(() => import("./pages/admin/AdminUsersPage"))
const AdminUserDetailPage = lazyRoute(() => import("./pages/admin/AdminUserDetailPage"))
const AgentPage = lazyRoute(() => import("./pages/admin/AgentPage"))
const AwsCostReportsPage = lazyRoute(() => import("./pages/admin/AwsCostReportsPage"))
const LaceworkTriagePage = lazyRoute(() => import("./pages/admin/LaceworkTriagePage"))
const E2eRunsPage = lazyRoute(() => import("./pages/admin/E2eRunsPage"))
const E2eRunDetailPage = lazyRoute(() => import("./pages/admin/E2eRunDetailPage"))
const E2ePlansPage = lazyRoute(() => import("./pages/admin/E2ePlansPage"))
const E2ePlanDetailPage = lazyRoute(() => import("./pages/admin/E2ePlanDetailPage"))
const E2eRepairsPage = lazyRoute(() => import("./pages/admin/E2eRepairsPage"))
const E2eGatePage = lazyRoute(() => import("./pages/admin/E2eGatePage"))
const AdminReportsPage = lazyRoute(() => import("./pages/admin/AdminReportsPage"))
const SimulatorPage = lazyRoute(() => import("./pages/admin/SimulatorPage"))
const AdminSitesPage = lazyRoute(() => import("./pages/admin/AdminSitesPage"))
const AdminBiddingReportPage = lazyRoute(() => import("./pages/admin/AdminBiddingReportPage"))
const AdminProgramsReportPage = lazyRoute(() => import("./pages/admin/AdminProgramsReportPage"))
const AdminBiddingPage = lazyRoute(() => import("./pages/admin/AdminBiddingPage"))
const DispatcherMonitorPage = lazyRoute(() => import("./pages/admin/DispatcherMonitorPage"))
const OptimizerPage = lazyRoute(() => import("./pages/admin/OptimizerPage"))
const DomainEntitiesPage = lazyRoute(() => import("./pages/admin/domain/DomainEntitiesPage"))
const SchemaErdPage = lazyRoute(() => import("./pages/admin/SchemaErdPage"))
const DocsPage = lazyRoute(() => import("./pages/admin/DocsPage"))
const FleetPage = lazyRoute(() => import("./pages/admin/FleetPage"))

/** Customer area moved from `…/fleet/*` to `…/assets/*`; keep old links working. */
function LegacyFleetRedirect() {
  const { pathname, search, hash } = useLocation()
  const target = legacyCustomerFleetRedirect(pathname)
  return target ? <Navigate to={`${target}${search}${hash}`} replace /> : null
}

/** Root layout: wraps all routes in providers so they render inside the router context. */
function AppLayout() {
  // Applies the GRIDBOX theme to <html> and follows the OS while on "system".
  const theme = useTheme()
  return (
    <AuthProvider>
      <TenantProvider>
        <Toaster position="top-right" richColors theme={theme} />
        {/* Top-level fallback: full-screen branded loader for initial load and
            public routes. Layouts add their own content-area boundary so in-app
            navigation keeps the shell visible. */}
        <Suspense fallback={<PageLoader className="min-h-screen" />}>
          <Outlet />
        </Suspense>
      </TenantProvider>
    </AuthProvider>
  )
}

// Combined-path customer routes via CUSTOMER_ROUTE_PATTERN; no basename.
export const appRoutes = createRoutesFromElements(
  <Route element={<AppLayout />}>
    {/* Public Routes */}
    <Route path="/login" element={<LoginPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />

    {/* Root Route - Smart Redirect */}
    <Route
      path="/"
      element={
        <AuthGuard>
          <RootRedirect />
        </AuthGuard>
      }
    />

    {/* Customer Picker */}
    <Route
      path="/customer-picker"
      element={
        <AuthGuard>
          <RequireCustomerApp>
            <CustomerPickerPage />
          </RequireCustomerApp>
        </AuthGuard>
      }
    />

    {/* Self-service account/profile — available to any authenticated user. */}
    <Route
      path="/account"
      element={
        <AuthGuard>
          <AccountPage />
        </AuthGuard>
      }
    />

    {/* Customer-scoped Routes — combined path so incomplete prefixes
          fall through to the 404 rather than rendering a blank outlet. */}
    <Route
      path={CUSTOMER_ROUTE_PATTERN}
      element={
        <AuthGuard>
          <RequireCustomerApp>
            <RootLayout />
          </RequireCustomerApp>
        </AuthGuard>
      }
    >
      <Route index element={<Navigate to="overview" replace />} />
      {/* Main Routes */}
      <Route path="overview" element={<OverviewPage />} />
      <Route path="reports" element={<ReportsPage />} />

      {/* Asset Routes (formerly "fleet"; old URLs redirect). */}
      <Route path="fleet/*" element={<LegacyFleetRedirect />} />
      <Route path="assets">
        <Route path="trip-schedule" element={<TripSchedulePage />} />
        <Route path="sites" element={<SitesPage />} />
        <Route path="sites/:siteId" element={<SiteDetailPage />} />
        <Route path="chargers" element={<ChargersPage />} />
        <Route path="devices" element={<CustomerDevicesPage />} />
        <Route path="chargers/:chargerId" element={<ChargerDetailPage />} />
        <Route path="charging-requirements" element={<ChargingRequirementsPage />} />
        <Route path="vehicles/:vehicleId" element={<VehicleDetailPage />} />
        <Route path="vehicles" element={<VehiclesPage />} />
        <Route path="batteries/maintenance" element={<BessMaintenancePage />} />
        <Route path="batteries/:batteryId" element={<BatteryDetailPage />} />
        <Route path="microgrid" element={<MicrogridPage />} />
        <Route path="batteries" element={<BatteriesPage />} />
        <Route path="rfid-cards" element={<RfidCardsPage />} />
      </Route>

      {/* Grid Services — tenant-scoped, global-admin-only (see above). */}
      <Route
        path="grid-services"
        element={
          <RequireGlobalAdmin>
            <GridServicesPage />
          </RequireGlobalAdmin>
        }
      />

      {/* Settings */}
      <Route path="company-settings" element={<CompanySettingsPage />} />
    </Route>

    {/* Admin Routes — any platform user on top of authenticated access.
          Per-section access is enforced in AdminLayout. */}
    <Route
      path="/admin"
      element={
        <AuthGuard>
          <RequirePlatformUser>
            <AdminEdgeGate>
              <AdminLayout />
            </AdminEdgeGate>
          </RequirePlatformUser>
        </AuthGuard>
      }
    >
      <Route index element={<AdminOverviewPage />} />
      <Route path="customers" element={<CustomersPage />} />
      <Route path="customers/:customerId" element={<AdminCustomerDetailPage />} />
      <Route path="bidding" element={<AdminBiddingPage />} />
      <Route path="dispatcher" element={<DispatcherMonitorPage />} />
      <Route path="optimizer" element={<OptimizerPage />} />
      <Route path="microgrid" element={<MicrogridPage />} />
      <Route path="reports/bidding" element={<AdminBiddingReportPage />} />
      <Route path="reports/market" element={<AdminBiddingReportPage market />} />
      <Route path="reports/programs" element={<AdminProgramsReportPage />} />
      <Route path="devices" element={<FleetPage />} />
      <Route path="sites" element={<AdminSitesPage />} />
      {/* Canonical path is /admin/devices; "fleet" kept so old links still resolve. */}
      <Route path="fleet" element={<Navigate to="/admin/devices" replace />} />
      <Route
        path="charging-requirements"
        element={<Navigate to="/admin/domain?panel=charging_requirements" replace />}
      />
      <Route path="domain" element={<DomainEntitiesPage />} />
      <Route path="schema-erd" element={<SchemaErdPage />} />
      <Route path="users" element={<AdminUsersPage />} />
      <Route path="users/:userId" element={<AdminUserDetailPage />} />
      <Route path="agent" element={<AgentPage />} />
      <Route path="aws-cost-reports" element={<AwsCostReportsPage />} />
      <Route path="lacework-triage" element={<LaceworkTriagePage />} />
      <Route path="e2e" element={<E2eRunsPage />} />
      <Route path="e2e/runs/:runId" element={<E2eRunDetailPage />} />
      <Route path="e2e/plans" element={<E2ePlansPage />} />
      <Route path="e2e/plans/:planId" element={<E2ePlanDetailPage />} />
      <Route path="e2e/repairs" element={<E2eRepairsPage />} />
      <Route path="e2e/gate" element={<E2eGatePage />} />
      <Route path="reports" element={<AdminReportsPage />} />
      <Route path="docs" element={<DocsPage />} />
      <Route path="docs/:slug" element={<DocsPage />} />
      {adminSimulatorEnabled && <Route path="simulator" element={<SimulatorPage />} />}
      {/* Scenario simulator (new) with the batch-run page as its second tab. */}
      <Route path="optimizer-simulator" element={<OptimizerSimulatorPage />} />
      <Route path="optimizer-simulator/:runId" element={<SimulationRunDetailPage />} />
    </Route>

    {/* 404 - Catch all */}
    <Route
      path="*"
      element={
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <h1 className="mb-2 font-display text-4xl font-bold text-content">404</h1>
            <p className="text-content-2">Page not found</p>
          </div>
        </div>
      }
    />
  </Route>
)

const router = createBrowserRouter(appRoutes)

export default function App() {
  // Start the topology-change watcher (store subscriptions + cross-tab
  // BroadcastChannel) at bootstrap, so mutations and other tabs' edits are
  // seen even before a view that reads useTopologyVersion() first mounts.
  // Idempotent; the hook still installs lazily for isolated test renders.
  useEffect(() => {
    installTopologySync()
  }, [])
  return <RouterProvider router={router} />
}
